<script setup lang="ts">
import { computed } from 'vue';
import {
  viewAxis, setViewAxis, axisAvailable, kdm, resetCurrentMatrixSlice, resetting,
  activeStakeholderId, activePivotTab, discussionState, getCommentIcon,
} from '@/store/kdm';

// Reset is owner / Group coordinator only (server re-checks).
const canReset = computed(() => {
  const cu = kdm.value?.current_user;
  return !!(cu?.is_owner || cu?.is_coordinator);
});

// Coordinates of the presented slice = the active value of the chosen axis,
// matching what Reset targets. SLICE level of the 4-level comment hierarchy
// (whole matrix → slice → cell → indicator); coords never collide with a cell
// (SH-QPI-TOC) or indicator (SH-QPI-TOC-ipr).
const sliceCoords = computed(() => {
  const axis = viewAxis.value;
  const pivotId = axis === 'stakeholder' ? activeStakeholderId.value : (activePivotTab.value?.id ?? null);
  return pivotId ? `slice-${axis}-${pivotId}` : '';
});
const sliceCommentIcon = computed(() => getCommentIcon(sliceCoords.value));

function openSliceDiscussion(): void {
  if (!sliceCoords.value) {
    return;
  }
  const cu = kdm.value?.current_user;
  discussionState.coords = sliceCoords.value;
  discussionState.nodeId = kdm.value?.nid || '';
  // Slice/whole-matrix comments are an owner/coordinator/editor concern.
  discussionState.canWrite = !!(cu?.is_owner || cu?.is_coordinator || cu?.is_editor);
  discussionState.canClear = !!(cu?.is_owner || cu?.is_coordinator);
  discussionState.visible = true;
}
</script>

<template>
  <div class="kdm-view-axis kdm-view-axis__btns" role="group" aria-label="View datasheets by axis" @click.stop>
    <button
      type="button"
      class="kdm-view-axis__btn"
      :class="{ active: viewAxis === 'stakeholder' }"
      @click="setViewAxis('stakeholder')"
      title="View by Stakeholder"
    >SH</button>
    <button
      type="button"
      class="kdm-view-axis__btn"
      :class="{ active: viewAxis === 'toc' }"
      :disabled="!axisAvailable('toc')"
      @click="setViewAxis('toc')"
      title="View by TOC"
    >TOC</button>
    <button
      type="button"
      class="kdm-view-axis__btn"
      :class="{ active: viewAxis === 'qpi' }"
      :disabled="!axisAvailable('qpi')"
      @click="setViewAxis('qpi')"
      title="View by QPI"
    >QPI</button>
    <!-- Comment on the presented slice (SLICE level) — before Reset. -->
    <button
      v-if="sliceCoords"
      type="button"
      class="kdm-view-axis__btn kdm-view-axis__comment"
      @click="openSliceDiscussion()"
      :title="`Comment on this slice (the presented ${viewAxis === 'stakeholder' ? 'Stakeholder' : viewAxis === 'toc' ? 'Object to Compare' : 'Enjeu'})`"
    >
      <i class="bi" :class="[sliceCommentIcon.icon, sliceCommentIcon.class]"></i>
    </button>
    <!-- Reset the presented slice (cell content + comments) — owner/coordinator. -->
    <button
      v-if="canReset"
      type="button"
      class="kdm-view-axis__btn kdm-view-axis__reset"
      :disabled="resetting"
      @click="resetCurrentMatrixSlice()"
      title="Reset this view — delete the cell content and comments of the presented slice"
    >
      <i class="bi bi-arrow-counterclockwise"></i> Reset
    </button>
  </div>
</template>

<style scoped>
.kdm-view-axis__btns {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.kdm-view-axis__btn {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.12s, color 0.12s;
}
.kdm-view-axis__btn.active {
  background: #fff;
  color: #495057;
  border-color: #fff;
}
.kdm-view-axis__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.kdm-view-axis__btn:not(.active):not(:disabled):hover {
  background: rgba(255, 255, 255, 0.2);
}
/* Slice comment button: legible on any toolbar background. */
.kdm-view-axis__comment {
  background: rgba(255, 255, 255, 0.92);
  color: #1d6fa5;
  border-color: rgba(255, 255, 255, 0.92);
}
.kdm-view-axis__comment:hover {
  background: #fff;
  color: #155a86;
}
/* Slice Reset: solid red so it stays legible (was a faint red, illegible on a
   light toolbar). */
.kdm-view-axis__reset {
  color: #fff;
  background: #e03131;
  border-color: #e03131;
}
.kdm-view-axis__reset:not(:disabled):hover {
  background: #c92a2a;
  border-color: #c92a2a;
  color: #fff;
}
</style>
